import { router } from "@inertiajs/react"
import { Check, ChevronDown } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { BrandLogo } from "@/components/brand-logo"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useIsMobile } from "@/hooks/use-mobile"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { cn } from "@/lib/utils"
import { currentBrandPath } from "@/routes"
import type { AvailableBrand, AvailableBrandState } from "@/types"

/**
 * OKS-513 — Per-crew summary line for the §11 profile switcher sheet:
 * rank · coins · missions for ONE crew, keyed by brand id. Optional — when
 * absent (every non-profile surface) the switcher renders exactly as before.
 */
export interface CrewSwitcherStat {
  rankName: string | null
  coins: number
  missions: number
}

interface CrewSwitcherProps {
  className?: string
  /**
   * OKS-513 — Optional per-crew stats keyed by brand id. When provided
   * (the §11 profile head), each row gains a "{rank} · {N} coins · {N}
   * missions" subtitle and the sheet shows the per-crew hint. Omitted
   * everywhere else → unchanged chrome (keeps the OKS-24 contract green).
   */
  crewStats?: Record<number, CrewSwitcherStat>
}

// cmdk group-heading / item styling previously applied inline by
// `CommandDialog` to its inner `<Command>`. Replicated verbatim on the shared
// `CrewSwitcherList` so headings/items keep identical spacing across both the
// Popover and Drawer chrome — replicating rather than editing `ui/command.tsx`
// (a ledgered edit is permitted with parity/a11y coverage — see
// `docs/frontend/README.md` rule 6 — but replication is cheaper here).
// (Input-wrapper selectors are inert here — the switcher has no `CommandInput`
// per OKS-24 §2 — but are kept 1:1 with the source to guarantee no drift.)
const COMMAND_CMDK_CLASS =
  "[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"

/**
 * CrewSwitcher — creator multi-crew context switcher.
 *
 * Visibility (CTPO contract): only when
 *   `currentUser.role === "creator"` AND `availableBrands.length > 1`.
 *
 * **Responsive chrome (OKS-159).** The panel content (a shared cmdk `Command`
 * body — see {@link CrewSwitcherList}) is identical across viewports; only the
 * wrapping container swaps, selected by {@link useIsMobile} (768px breakpoint):
 *   - **≥768px →** Radix `Popover` anchored to the trigger chip (non-modal:
 *     no focus trap, dismiss on Escape/outside-click).
 *   - **<768px →** `ui/drawer` bottom sheet (Base UI `Drawer` since OKS-1022;
 *     modal: focus trap + scroll lock, swipe-to-dismiss).
 * Both return focus to the trigger on close (PRD OQ2 — an accepted, documented
 * modality difference, not a regression).
 *
 * Lists crews grouped into "Active crew" (current) and "Other crews"
 * (siblings). The functional contract is frozen across both variants: all
 * testids, copy, group structure, 3-state pills, and selection logic are
 * preserved identically.
 *
 * **OKS-24 deltas:**
 *   - Each item carries a 3-state membership pill (`active` / `onboarding` /
 *     `charter_pending`) reflecting `availableBrands[i].state`. Color is never
 *     sole indicator — the pill label is mandatory (a11y).
 *   - Each `<CommandItem>` carries `data-membership-state` so QA can assert
 *     state without brittle visual selectors.
 *   - Search input slot is HIDDEN — invite-only flow ships ≤5 memberships in
 *     practice, typeahead is noise (UX Spec §2 V1 simplification).
 *   - Footer hint reminds creators that new crews come via invite link.
 *
 * Selecting a crew POSTs to `currentBrandPath()` with `preserveState: false`
 * → backend rewrites session → full-page reload into that brand's creator
 * context.
 */
export function CrewSwitcher({ className, crewStats }: CrewSwitcherProps) {
  const { currentUser, currentBrand, availableBrands, activeBrandId } =
    useCurrentContext()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  const isVisible =
    currentUser?.role === "creator" && availableBrands.length > 1

  if (!isVisible) return null

  // OKS-24 — `activeBrandId` is the canonical "active crew" pointer for
  // creators (drives wizard server dispatch). On surfaces where the
  // server has set it, prefer it over `currentBrand?.id` (which can lag
  // mid-wizard if the wizard chrome inherits from a non-creator-context
  // ancestor visit). Both fall back to each other.
  const activeId = activeBrandId ?? currentBrand?.id ?? null
  const activeBrand =
    availableBrands.find((brand) => brand.id === activeId) ?? null
  const otherCrews = availableBrands.filter((brand) => brand.id !== activeId)

  function handleSelect(brand: AvailableBrand) {
    if (activeId === brand.id) {
      setOpen(false)
      return
    }
    setOpen(false)
    router.post(
      currentBrandPath(),
      { brand_id: brand.id },
      {
        preserveScroll: false,
        preserveState: false,
        onError: () => {
          toast.error(t("crew_switcher.error_toast"))
        },
      },
    )
  }

  const triggerLabel = t("crew_switcher.trigger_label", {
    name: activeBrand?.name ?? currentBrand?.name ?? "",
  })

  // Shared trigger chip — rendered identically as the `render` element of
  // either `PopoverTrigger` or `DrawerTrigger`. The primitive trigger owns the
  // open toggle via `onOpenChange`; we keep `aria-expanded={open}` verbatim
  // (frozen contract) — it mirrors the primitive's own state.
  const trigger = (
    <button
      type="button"
      data-testid="crew-switcher-trigger"
      aria-label={triggerLabel}
      aria-haspopup="dialog"
      aria-expanded={open}
      className={cn(
        // ≥44px touch target per a11y spec
        "border-input hover:bg-accent focus-visible:ring-ring flex min-h-11 min-w-0 items-center gap-2 rounded-full border bg-transparent px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none",
        className,
      )}
    >
      {(activeBrand ?? currentBrand) && (
        <CrewAvatar brand={activeBrand ?? currentBrand!} />
      )}
      <span className="max-w-[14ch] truncate font-medium">
        {activeBrand?.name ?? currentBrand?.name}
      </span>
      <ChevronDown className="size-4 opacity-60" aria-hidden="true" />
    </button>
  )

  const list = (
    <CrewSwitcherList
      activeBrand={activeBrand}
      otherCrews={otherCrews}
      onSelect={handleSelect}
      crewStats={crewStats}
    />
  )

  // <768px — `ui/drawer` bottom sheet (modal). sr-only header gives the
  // dialog its accessible title + description; reuses the existing
  // `dialog_title` / `dialog_description` i18n keys.
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger render={trigger} />
        <DrawerContent
          data-testid="crew-switcher-drawer"
          className="pb-[env(safe-area-inset-bottom)]"
        >
          <DrawerHeader className="sr-only">
            <DrawerTitle>{t("crew_switcher.dialog_title")}</DrawerTitle>
            <DrawerDescription>
              {t("crew_switcher.dialog_description")}
            </DrawerDescription>
          </DrawerHeader>
          {list}
        </DrawerContent>
      </Drawer>
    )
  }

  // ≥768px — Radix Popover anchored to the trigger (non-modal). No visible
  // title needed; `aria-label` names the content for AT.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger} />
      <PopoverContent
        data-testid="crew-switcher-popover"
        align="start"
        aria-label={t("crew_switcher.dialog_title")}
        className="w-72 p-0"
      >
        {list}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Shared cmdk `Command` body for both the Popover and Drawer variants
 * (PRD D2). Keeping a single source for the list — groups, items, pills,
 * `CommandEmpty`, footer — guarantees keyboard nav, `onSelect`, and the frozen
 * testid contract stay identical no matter which chrome wraps it.
 */
function CrewSwitcherList({
  activeBrand,
  otherCrews,
  onSelect,
  crewStats,
}: {
  activeBrand: AvailableBrand | null
  otherCrews: AvailableBrand[]
  onSelect: (brand: AvailableBrand) => void
  crewStats?: Record<number, CrewSwitcherStat>
}) {
  const { t } = useTranslation()

  return (
    <Command className={COMMAND_CMDK_CLASS}>
      {/* OKS-513 — per-crew hint, shown ONLY in the §11 enriched variant. */}
      {crewStats && (
        <p
          data-testid="crew-switcher-per-crew-hint"
          className="text-muted-foreground px-4 pt-3 pb-1 text-xs"
        >
          {t("crew_switcher.per_crew_hint")}
        </p>
      )}
      {/*
        OKS-24 — search input intentionally OMITTED. UX Spec §2 V1
        simplification: invite-only flow ships ≤5 rows in practice; typeahead
        UI adds visual weight without payoff. Re-introducing it is the deferred
        follow-up; the Vitest `command-input-wrapper` absence guard locks this.
      */}
      <CommandList>
        <CommandEmpty>{t("crew_switcher.empty")}</CommandEmpty>
        {activeBrand && (
          <CommandGroup heading={t("crew_switcher.group_heading_active")}>
            <CommandItem
              key={activeBrand.id}
              value={`${activeBrand.name} ${activeBrand.slug}`}
              onSelect={() => {
                onSelect(activeBrand)
              }}
              data-testid={`crew-switcher-item-${activeBrand.id}`}
              data-membership-state={activeBrand.state ?? "active"}
              aria-current="true"
              className="min-h-11"
            >
              <CrewAvatar brand={activeBrand} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{activeBrand.name}</span>
                <CrewStatsLine
                  stat={crewStats?.[activeBrand.id]}
                  brandId={activeBrand.id}
                />
              </span>
              <CrewStatePill brand={activeBrand} />
              <Check
                aria-label={t("crew_switcher.current_indicator")}
                className="ml-1 size-4"
              />
              <span className="sr-only">
                {t("crew_switcher.current_indicator")}
              </span>
            </CommandItem>
          </CommandGroup>
        )}
        {otherCrews.length > 0 && (
          <CommandGroup heading={t("crew_switcher.group_heading_other")}>
            {otherCrews.map((brand) => (
              <CommandItem
                key={brand.id}
                value={`${brand.name} ${brand.slug}`}
                onSelect={() => {
                  onSelect(brand)
                }}
                data-testid={`crew-switcher-item-${brand.id}`}
                data-membership-state={brand.state ?? "active"}
                className="min-h-11"
              >
                <CrewAvatar brand={brand} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{brand.name}</span>
                  <CrewStatsLine
                    stat={crewStats?.[brand.id]}
                    brandId={brand.id}
                  />
                </span>
                <CrewStatePill brand={brand} />
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
      <p
        data-testid="crew-switcher-footer-hint"
        className="text-muted-foreground border-t px-4 py-3 text-xs"
      >
        {t("crew_switcher.footer_invite_hint")}
      </p>
    </Command>
  )
}

/**
 * OKS-513 — "{rank} · {N} coins · {N} missions" subtitle under a crew row,
 * rendered only when the §11 profile passes a stat for this brand. Coins
 * abbreviate (56K) via the shared compact formatter. Rank may be null (a
 * brand-new crew with no ranks) → the rank token is dropped.
 */
function CrewStatsLine({
  stat,
  brandId,
}: {
  stat?: CrewSwitcherStat
  brandId: number
}) {
  const { t } = useTranslation()
  if (!stat) return null

  const params = {
    rank: stat.rankName,
    coins: formatCompactNumber(stat.coins),
    missions: stat.missions,
  }

  return (
    <span
      data-testid={`crew-switcher-item-${brandId}-stats`}
      className="text-muted-foreground truncate text-xs"
    >
      {stat.rankName
        ? t("crew_switcher.crew_stats", params)
        : t("crew_switcher.crew_stats_no_rank", params)}
    </span>
  )
}

/**
 * CrewAvatar — chrome for a single crew row in the switcher list.
 *
 * OKS-316 — Migrated from a bespoke `<img>` / monogram `<div>` block
 * onto the shared `<BrandLogo>` wrapper (OKS-303 single source of
 * truth for brand chrome). Size + shape preserved verbatim via the
 * `className` prop; `alt=""` because the adjacent brand name span is
 * the textual identity.
 */
function CrewAvatar({ brand }: { brand: AvailableBrand }) {
  return (
    <BrandLogo
      logoUrl={brand.logo_url}
      name={brand.name}
      className="size-7 shrink-0 rounded-full"
      testId="crew-switcher-logo"
    />
  )
}

const STATE_PILL_CLASSES: Record<AvailableBrandState, string> = {
  // Color is never sole indicator — labels are mandatory. Tones below
  // are advisory; QA asserts on the literal label, not the class.
  //
  // OKS-676 — `active` is retained here (the record must stay exhaustive over
  // `AvailableBrandState`) but is no longer rendered: `CrewStatePill` short-
  // circuits for `active`, so this tone is dead. Kept so a revert to option (a)
  // (re-show the active membership pill, recoloured/relabelled) stays a one-line
  // change. `crew_switcher.state_pill.active` i18n key is likewise retained.
  active:
    "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200",
  onboarding:
    "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200",
  charter_pending: "border-border bg-muted text-muted-foreground",
}

function CrewStatePill({ brand }: { brand: AvailableBrand }) {
  const { t } = useTranslation()
  // Server omits `state` for non-creator switcher contexts (legacy OKS-13
  // brand/agency switcher). Pill renders nothing in that case — the
  // CommandItem still carries `data-membership-state="active"` as a
  // backwards-compat fallback (see parent).
  if (!brand.state) return null
  // OKS-676 — `active` (fully-joined) is the normal/happy state and carries no
  // actionable signal. Its emerald "Active" pill collided with the "Active crew"
  // selected heading, making every active-member crew read as "selected". The
  // current crew is already signalled by the check + `aria-current` + heading, so
  // the pill only earns its place when a membership is INCOMPLETE (needs action).
  if (brand.state === "active") return null
  const label = t(`crew_switcher.state_pill.${brand.state}`)
  return (
    <span
      data-testid={`crew-switcher-item-${brand.id}-state-pill`}
      data-membership-state={brand.state}
      className={cn(
        "ml-auto inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        STATE_PILL_CLASSES[brand.state],
      )}
    >
      {label}
    </span>
  )
}
